import React, { useCallback, useState } from "react";
import { DA_Address } from "@danishagro/shared/src/interfaces/address.interface";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { DA_Input } from "@danishagro/shared/src/components/atoms/Input/Input.component";
import { useTranslations } from "../../../contexts/translations/translations.context";
import {
    DA_DynamicOverlayWrapper,
    useDynamicOverlay,
} from "../../../hooks/dynamicOverlay/useDynamicOverlay.hook";
import { useScreen } from "../../../contexts/screen.context";
import { useAddressFromSessionStorage } from "../../../hooks/useAddressFromSessionStorage.hook";
import { useAddressesApi } from "../../../hooks/api/useAddressesApi.hook";
import { useValidation } from "../AddressOverlay/hooks/useValidation.hook";
import S from "./AddressForm.module.scss";

export interface DA_AddressFormProps {
    onSubmit: (address: DA_Address) => void;
    onCancel?: () => void;
    submitText?: string;
    title?: string;
    description?: string;
    setAddresses?: React.Dispatch<React.SetStateAction<DA_Address[]>>;
    isModalMobile?: boolean;
}

export const DA_AddressForm = (props: DA_AddressFormProps) => {
    const { onSubmit, onCancel, submitText, description, setAddresses } = props;

    const [awaitingResponse, setAwaitingResponse] = useState(false);
    const [addressAddError, setAddressAddError] = useState<string | null>(null);

    const [customAddressName, setCustomAddressDescription] = useState("");
    const [customStreetName, setCustomStreetName] = useState("");
    const [customStreetNumber, setCustomStreetNumber] = useState("");
    const [customZip, setCustomZip] = useState("");
    const [customCity, setCustomCity] = useState("");

    const { getDictionaryItem, getDictionaryString } = useTranslations();
    const { isMobileOrTablet } = useScreen();
    const { closeOverlay } = useDynamicOverlay();
    const { addDeliveryAddress } = useAddressesApi();
    const { setAddressInSessionStorage } = useAddressFromSessionStorage();

    const { validationErrorMessages, validate } = useValidation(
        customAddressName,
        customStreetName,
        customStreetNumber,
        customZip,
        customCity
    );

    const onSubmitButtonClicked = useCallback(() => {
        // Reset any previous error message
        setAddressAddError(null);

        const isValid = validate();

        if (isValid) {
            setAwaitingResponse(true);

            addDeliveryAddress({
                name: customAddressName.trim(),
                streetName: customStreetName.trim(),
                streetNumber: customStreetNumber.trim(),
                zipCode: customZip.trim(),
                city: customCity.trim(),
                countryCode: "DK",
            })
                .then((newAddress) => {
                    onSubmit(newAddress);
                    setAddressInSessionStorage(newAddress.id);

                    if (setAddresses) {
                        setAddresses((prev) => [...prev, newAddress]);
                    }
                })
                .catch((error) => {
                    // Check if the error is a 409 Conflict
                    if (error.status === 409) {
                        setAddressAddError(
                            getDictionaryString("addressModalValidationAddressAlreadyExists")
                        );
                    } else {
                        // Handle other errors if necessary
                        console.log("An unexpected error occurred.");
                    }
                })
                .finally(() => setAwaitingResponse(false));
        }
    }, [
        validate,
        addDeliveryAddress,
        customAddressName,
        customStreetName,
        customStreetNumber,
        customZip,
        customCity,
        onSubmit,
        setAddressInSessionStorage,
        setAddresses,
        getDictionaryString,
    ]);

    const onCancelButtonClicked = useCallback(() => {
        if (onCancel) {
            onCancel();
        } else {
            closeOverlay();
        }
    }, [onCancel, closeOverlay]);

    const addressNameError = validationErrorMessages.find((x) => x.key === "addressName");
    const streetNameError = validationErrorMessages.find((x) => x.key === "streetName");
    const streetNumberError = validationErrorMessages.find((x) => x.key === "streetNumber");
    const zipCodeError = validationErrorMessages.find((x) => x.key === "zipCode");
    const cityError = validationErrorMessages.find((x) => x.key === "city");

    return (
        <DA_DynamicOverlayWrapper
            content={
                <div className={S.wrapper}>
                    {description ? <div className={S.description}>{description}</div> : null}

                    <div className={S.addAddress}>
                        <div className={S.addressForm}>
                            <div className={S.addressDescription}>
                                <DA_Input
                                    label={getDictionaryItem("addressName")}
                                    id="address-form-address-name"
                                    invalid={!!addressNameError}
                                    value={customAddressName}
                                    onChange={(newValue) => setCustomAddressDescription(newValue)}
                                />
                            </div>
                            <div className={S.street}>
                                <div className={S.streetName}>
                                    <DA_Input
                                        label={getDictionaryItem("streetName")}
                                        id="address-form-street-name"
                                        invalid={!!streetNameError}
                                        value={customStreetName}
                                        onChange={(newValue) => setCustomStreetName(newValue)}
                                    />
                                </div>
                                <div className={S.streetNumber}>
                                    <DA_Input
                                        label={getDictionaryItem("streetNumber")}
                                        id="address-form-street-number"
                                        invalid={!!streetNumberError}
                                        value={customStreetNumber}
                                        onChange={(newValue) => setCustomStreetNumber(newValue)}
                                    />
                                </div>
                            </div>
                            <div className={S.zipCity}>
                                <div className={S.zip}>
                                    <DA_Input
                                        pattern="[0-9]*"
                                        inputMode="numeric"
                                        type="text"
                                        label={getDictionaryItem("siloAndHouses.zip")}
                                        id="address-form-zip"
                                        invalid={!!zipCodeError}
                                        value={customZip}
                                        onChange={(newValue) => setCustomZip(newValue)}
                                    />
                                </div>
                                <div className={S.city}>
                                    <DA_Input
                                        label={getDictionaryItem("city")}
                                        id="address-form-city"
                                        invalid={!!cityError}
                                        value={customCity}
                                        onChange={(newValue) => setCustomCity(newValue)}
                                    />
                                </div>
                            </div>

                            {/** Validation */}
                            {validationErrorMessages?.length > 0 ? (
                                <div className={S.errorMessage}>
                                    {validationErrorMessages?.[0].value}
                                </div>
                            ) : null}

                            {/** Error message */}
                            {addressAddError && (
                                <div className={S.errorMessage}>{addressAddError}</div>
                            )}
                        </div>
                    </div>
                </div>
            }
            footer={
                <div className={S.buttons}>
                    {/** Submit */}
                    <DA_Button
                        title={submitText || getDictionaryString("add")}
                        onClick={onSubmitButtonClicked}
                        disabled={awaitingResponse}
                        showSpinner={awaitingResponse}
                        fullWidth={isMobileOrTablet}
                    />

                    {/** Cancel */}
                    <DA_Button
                        title={getDictionaryString("cancel")}
                        onClick={onCancelButtonClicked}
                        isGhost
                        disabled={awaitingResponse}
                        fullWidth={isMobileOrTablet}
                    />
                </div>
            }
            isModalMobile={props.isModalMobile}
        />
    );
};
